import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  type DataSourceTableViewLayoutState,
  type DataSourceViewLayoutState,
  type DataSourceViewWindowState,
} from '@blocksuite/data-view';
import type { DatabaseShardManifestEntry } from '@streamlain/database-identity';
import type {
  DocPublicationStorage,
  LocalPublicationReceipt,
  PublicationKind,
} from '@streamlain/doc-publication-contract';
import type { DatabaseRowEdit } from '@streamlain/doc-publication-contract/database-writer';
import type { Doc as YDoc } from 'yjs';

import type { DatabaseBlockDataSource } from './data-source.js';
import type { DatabaseSemanticChange } from './data-source-history.js';

export interface DatabaseLocalRow {
  readonly rowId: string;
  readonly cells: RowShardCells;
}

export interface DatabaseLocalRowChange {
  readonly doc: { readonly id: string; readonly spaceDoc: YDoc };
  readonly rows: readonly DatabaseLocalRow[];
  /** Semantic baseline captured before queuing; never a speculative live edit. */
  readonly before?: readonly DatabaseLocalRow[];
}

/** Internal writer input, shared by editor, import, schema and relation adapters. */
export interface DatabaseRowMutation {
  readonly kind: PublicationKind;
  readonly rowEdits?: ReadonlyMap<
    DatabaseShardManifestEntry,
    {
      readonly doc: DatabaseLocalRowChange['doc'];
      readonly edits: readonly DatabaseRowEdit[];
    }
  >;
  readonly changes: ReadonlyMap<
    DatabaseShardManifestEntry,
    DatabaseLocalRowChange
  >;
  /** Immutable FileIndex source snapshot; its diff is computed inside the writer tail. */
  readonly mirror?: {
    readonly sourcePropertyId: string;
    readonly rows: readonly DatabaseRowCellValueInsert[];
    /** Rows of source blocks placed in page parts that are not loaded: the mirror cannot see them and keeps them. */
    readonly retainedRowIds: ReadonlySet<string>;
    readonly planned: (result: {
      upserts: { rowId: string; created: boolean }[];
      deletedRowIds: string[];
    }) => void;
  };
  readonly baseShards?: readonly DatabaseShardManifestEntry[];
  readonly rootFields?: Readonly<Record<string, unknown>>;
  /** Existing schema helpers execute against an isolated root model only. */
  readonly rootMutation?: (stage: DatabaseBlockModel) => void;
  /** Local-field append after rootMutation on one stage; relation/FileIndex intents use their own planners. */
  readonly createRows?: {
    readonly position: 'end';
    readonly actor: DataSourceWriteActor;
    readonly values: (
      stage: DatabaseBlockModel
    ) => readonly DatabaseRowCellValueInsert[];
  };
  readonly interactive?: boolean;
  /** Layout change caused by the previous write (shard merge): it joins that
   * write's undo entry, so undo restores the layout the write addressed, and
   * stays out of flushLocalWrites, whose callers wait only for their own data. */
  readonly maintenance?: boolean;
  /** Semantic inverse/original from a confirmed stage, never a cached Yjs delta. */
  readonly restore?: readonly DatabaseSemanticChange[];
}

export type DatabasePublicationPort = Pick<
  DocPublicationStorage,
  'readPublicationBase' | 'commitPublication'
>;

/** Explicit server follow-up boundary; local writer completion never invokes it. */
export interface DatabasePublicationScope {
  settle(signal?: AbortSignal): Promise<readonly LocalPublicationReceipt[]>;
}

export interface DatabasePublicationSyncPort {
  waitForPublicationsSynced(
    receipts: readonly LocalPublicationReceipt[],
    signal?: AbortSignal,
    /** Called once per receipt document the server confirmed. */
    onDocSynced?: (docId: string) => void
  ): Promise<void>;
}

/** Resolves null when the mutation found nothing to change and published nothing. */
export type DatabaseRowMutationCommit = (
  input: DatabaseRowMutation,
  signal?: AbortSignal
) => Promise<LocalPublicationReceipt | null>;

export interface DatabaseRelationInverseEdit {
  readonly target: { readonly docId: string; readonly databaseId: string };
  readonly rowId: string;
  readonly fieldId: string;
  readonly remove: readonly string[];
  readonly add: readonly string[];
  readonly replace: boolean;
}

export interface DatabaseRelationInversePlanner {
  plan(
    input: {
      source: { docId: string; databaseId: string };
      edits: ReadonlyArray<{
        rowId: string;
        propertyId: string;
        before: readonly string[];
        after: readonly string[];
      }>;
    },
    signal?: AbortSignal
  ): Promise<ReadonlyArray<DatabaseRelationInverseEdit>>;
}

export class DatabasePublicationBaseUnavailable extends Error {
  readonly code = 'BASE_UNAVAILABLE';
  constructor() {
    super('BASE_UNAVAILABLE');
    this.name = 'DatabasePublicationBaseUnavailable';
  }
}

export type SpacialProperty = {
  valueSet: (rowId: string, propertyId: string, value: unknown) => void;
  valueGet: (rowId: string, propertyId: string) => unknown;
};

export type DataSourceWriteActor = 'user' | 'automation' | 'system';

export type DatabaseFileCellReviewCopyRow = {
  rowId: string;
  refId: string;
  blobId: string;
};

export type DatabaseRowShardDocLoadReason =
  | 'row-window'
  | 'relation'
  | 'write'
  | 'directory';

export type DatabaseRowShardDocLoadContext = {
  kind: 'database-row-shard';
  blockId: string;
  databaseId: string;
  reason: DatabaseRowShardDocLoadReason;
  workspaceId: string;
};

export type DatabaseRowShardReadiness =
  | { progress?: DatabaseRowShardHydrationProgress; status: 'ready' }
  | {
      /**
       * A read of the root or a window shard failed terminally; the rows
       * are not shown until a later read completes.
       */
      incomplete?: true;
      progress?: DatabaseRowShardHydrationProgress;
      status: 'loading';
    }
  | {
      message: string;
      /** The server confirmed it holds no data for a required row shard. */
      missing?: boolean;
      repairable: boolean;
      status: 'error';
    };

export type DatabaseRowShardHydrationProgress = {
  loadedRows: number;
  readyShards: number;
  totalRows: number;
  totalShards: number;
};

export type DatabaseRowShardRepairResult = {
  repairedShardCount: number;
  rowCount: number;
};

export type DatabaseRowShardIntegrityIssue = {
  message: string;
  repairable: boolean;
  rowCount: number;
  shardId: string;
};

export type DatabaseRowShardDocLoader = (
  docId: string,
  context: DatabaseRowShardDocLoadContext
) => void | (() => void);

/**
 * Watches a database system document (root or row shard) and calls
 * `onMissing` once it is known to be absent: local storage is loaded and the
 * remote sync finished without delivering any data, or set the document apart
 * after a failed read. Returns a disposer.
 */
export type DatabaseSystemDocMissingObserver = (
  docId: string,
  onMissing: () => void
) => () => void;

/**
 * Publication state of one database system document on this device.
 * `current`: every server state announced for it is pulled, or the device
 * is offline and only its own complete local writes exist. `pending`: an
 * announced newer state is still being read. `incomplete`: the read failed
 * terminally.
 */
export type DatabasePublicationViewState = 'current' | 'pending' | 'incomplete';

/**
 * Reports the publication state of a database system document through
 * `onState`, first as soon as it is known. Returns a disposer.
 */
export type DatabasePublicationViewObserver = (
  docId: string,
  onState: (state: DatabasePublicationViewState) => void
) => () => void;

/**
 * Server materialization of the access doc a system-doc binding names.
 * Returns `null` when the server already holds the doc, otherwise a promise
 * that settles `true` once it does and `false` when the wait ended without it.
 */
/** Database storage the browser could not load from the server. */
export type DatabaseMissingStorage = 'root' | 'rows';

export type DatabaseRepairRequest = {
  blockId: string;
  databaseId: string;
  workspaceId: string;
  missing: DatabaseMissingStorage;
  /** Root and missing row-shard doc ids known to the block. */
  docIds: readonly string[];
};

/**
 * - `requeued`: the server released stuck updates and persists them now.
 * - `persisting`: the server holds pending updates and keeps retrying.
 * - `root_not_received`: the server never got the database root.
 * - `healthy`: the server holds every document; the browser reloads them.
 * - `failed`: the repair request itself failed.
 */
export type DatabaseRepairOutcome =
  | 'requeued'
  | 'persisting'
  | 'root_not_received'
  | 'healthy'
  | 'failed';

export type DatabaseRepairResult = {
  outcome: DatabaseRepairOutcome;
  /** Automated admin report of this restore; null when none was recorded. */
  reportId: string | null;
};

/** Host actions offered where a database is missing data. */
export type DatabaseRepairActions = {
  restore: (request: DatabaseRepairRequest) => Promise<DatabaseRepairResult>;
  /** The missing data arrived after a restore; completes its report. */
  confirmRestored: (reportId: string, elapsedMs: number) => void;
  reportBug: (request: DatabaseRepairRequest) => void;
};

/** Why the server refused changes of this database for good. */
export type DatabaseRefusedChangeReason =
  | 'FORBIDDEN'
  | 'REMOTE_CONFLICT'
  | 'FORMAT_MISMATCH'
  | 'LIMIT_EXCEEDED';

export type DatabaseRefusedChangeRequest = {
  blockId: string;
  databaseId: string;
  workspaceId: string;
  /** Root and row-shard doc ids known to the block. */
  docIds: readonly string[];
};

/**
 * Host actions for changes the server refused for good. They stay on the
 * device and are sent or deleted only when the user decides; every action
 * works on local storage alone.
 */
export type DatabaseRefusedChangeActions = {
  /** Reports the stored reason now and on every change; returns the stop. */
  watch: (
    getRequest: () => DatabaseRefusedChangeRequest | null,
    onChange: (reason: DatabaseRefusedChangeReason | null) => void
  ) => () => void;
  /** Queues the refused changes as a new attempt on the current server state. */
  resend: (request: DatabaseRefusedChangeRequest) => Promise<void>;
  /** Deletes the local changes; the server state is loaded again. */
  discard: (request: DatabaseRefusedChangeRequest) => Promise<void>;
};

export type DatabaseRowShardSnapshotBarrierContext = {
  blockId: string;
  databaseId: string;
  rootDocId: string;
  rowShardDocIds: readonly string[];
  signal: AbortSignal;
  workspaceId: string;
};

/**
 * Write durability barrier: resolves once local publications of the listed
 * documents are durable. It never waits for the server.
 */
export type DatabaseRowShardWriteBarrier = (
  context: DatabaseRowShardSnapshotBarrierContext
) => Promise<void>;

export type DatabaseUiStatePersistence = 'durable' | 'local' | 'transient';

export type DatabaseLocalUiState = {
  tableLayouts?: Record<string, DataSourceTableViewLayoutState>;
  viewLayouts?: Record<string, DataSourceViewLayoutState>;
  viewId?: string;
  windows?: Record<string, DataSourceViewWindowState>;
};

export type RowShardCell = {
  columnId: string;
  value: unknown;
};

export type RowShardCells = Record<string, RowShardCell>;

export type ShardedRowRecord = {
  id: string;
  cells: RowShardCells;
};

export type ShardedRowsDocCacheEntry = {
  rows: ShardedRowRecord[];
  revision: number;
  /** Lineage of the columns the rows were projected with. */
  lineage: string;
};

export type DatabaseRowCellValueInsert = {
  rowId: string;
  values: Record<string, unknown>;
  /**
   * Only for an existing row of a database on the row edit port: stored cell
   * values the write requires, by column id. Another value, or no such row,
   * fails the publication with LOCAL_CONFLICT.
   */
  expected?: Readonly<Record<string, unknown>>;
};

/** Rows one publication creates or changes, and rows it deletes. */
export type DatabaseRowSet = {
  readonly upserts: readonly DatabaseRowCellValueInsert[];
  readonly deleteRowIds: readonly string[];
};

export type ReactiveShardedCellValue = {
  dispose: () => void;
  propertyId: string;
  propertyType: string;
  rowId: string;
  serialized: unknown;
  value: unknown;
};

export type RawValueJsonConverter = {
  config: {
    jsonValue: {
      schema?: {
        safeParse: (
          value: unknown
        ) => { success: true; data: unknown } | { success: false };
      };
    };
    rawValue: {
      fromJson?: (config: {
        data: Record<string, unknown>;
        dataSource: DatabaseBlockDataSource;
        value: unknown;
      }) => unknown;
    };
  };
};
